import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

export const TwoFactorLogin = ({ tempToken, onSuccess, onBack }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/2fa/validate`, {
        temp_token: tempToken,
        code: code
      });
      
      // Check for warning about backup code usage
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      
      onSuccess(response.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid authentication code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src={NBR_LOGO} 
            alt="National Business Register" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
            <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
          </div>
        </div>

        {/* 2FA Verification Card */}
        <div className="swiss-card p-8" data-testid="2fa-login-card">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900">Two-Factor Authentication</h2>
            <p className="text-slate-500 text-sm mt-2">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2" data-testid="2fa-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="code">Authentication Code</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="h-14 text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                autoFocus
                disabled={loading}
                data-testid="2fa-code-input"
              />
              <p className="text-xs text-slate-400 text-center">
                Or enter a backup code if you've lost access to your authenticator
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              disabled={code.length < 6 || loading}
              data-testid="2fa-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Verify
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button 
              onClick={onBack}
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center justify-center gap-1 mx-auto"
              data-testid="2fa-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          UK Trademark Monitoring System
        </p>
      </div>
    </div>
  );
};
